import { ButtonProps } from '@engyalo/delivery-ui-components/lib/ui/components/Button'
import { DefaultProps } from '../../../interfaces'
import deepMerge from '../../../utils/deepMerge'
import {
  CinnamonActionNames,
  DefaultModalContainerNames,
  TemplateActionNames,
  TextLibrary,
} from '../defaultConstants'
import { PriceSimplified } from './childProps/Price'

export const ButtonDefault: DefaultProps<ButtonProps> = {}

export const ButtonFiltersCancel = deepMerge(ButtonDefault, {
  label: TextLibrary.FILTERS_MODAL_BUTTON_CANCEL,
  styles: {
    minWidth: '128px',
    background: '#FFFFFF',
    border: '1px solid #BABABA',
    color: '#2C2C2C',
    ':hover': {
      background: '#FFFFFF',
      border: '1px solid #BABABA',
      color: '#2C2C2C',
    },
  },
  onClick: {
    cinnamonActions: [
      {
        name: CinnamonActionNames.CLOSE_MODAL,
        args: {
          container: DefaultModalContainerNames.FILTERS_CLEAR_CONFIRM,
        },
      },
    ],
  } as any,
})

export const ButtonFiltersClear = deepMerge(ButtonDefault, {
  label: TextLibrary.FILTERS_MODAL_BUTTON_OK,
  styles: {
    minWidth: '128px',
  },
  onClick: {
    cinnamonActions: [
      {
        name: TemplateActionNames.FILTERS_CLEAR_FILTERS,
      },
      {
        name: CinnamonActionNames.CLOSE_MODAL,
        args: {
          container: DefaultModalContainerNames.FILTERS_CLEAR_CONFIRM,
        },
      },
    ],
  } as any,
})

/**
 * The stock cart footer's CTA — `shoppingcart-footer-001` reaches this through
 * `MainFooterCartPriceDetailed`, on every storefront that has not re-authored
 * its footer.
 *
 * Its copy lives in `startLabel` (the cart total, with the price beside it)
 * and `endLabel` (the item count, with a `CmArrowCircleRight`). The centre
 * `label` is empty by design: the layout is total-left / count-right, with no
 * word between them.
 *
 * ⚠️ **That empty string is load-bearing.** `MainFooterCartPriceDetailed` is
 * `deepMerge(MainFooterCart, { button: ButtonCartPriceDetailed })`, and
 * `deepMerge` lets the second operand's scalar win per key — so this `''`
 * overwrites `TextLibrary.CART_PLACE_BUTTON`. That is the whole of COM-803:
 * the cart CTA label is authorable, and invisible here.
 *
 * ⚠️ **Do not "fix" the blank by binding the token here.** A centre word would
 * appear between the total and the item count on every storefront in the
 * estate, from a change that reads as a typo repair. A storefront that wants
 * the worded CTA names `'MainFooterCart'` as its `defaultProps`, or authors
 * `props.button.label` on the component.
 *
 * ⚠️ **The two labels fail differently.** `label` is a whole-value token, so
 * an unresolved one becomes `undefined` and renders blank. `endLabel.label`
 * composes `{{cart.itemsTotal}}` with `CART_ITEMS_COUNTER`, and the compose
 * branch prints `String(result ?? input)` — so an unresolved one puts the raw
 * `{{texts.cart.itemsCounter}}` in front of a shopper.
 *
 * Pinned in `tests/cartPlaceButtonAuthoring.test.ts`.
 */
export const ButtonCartPriceDetailed: ButtonProps = {
  color: 'primary',
  label: '',
  size: 'large',
  startLabel: {
    counter: false,
    label: TextLibrary.CART_TOTAL_LABEL,
    price: {
      ...PriceSimplified,
      value: '{{cart.subtotal}}' as any,
      discount: '{{cart.total}}' as any,
      display: '{{globals.footer.priceDisplay}}',
      fee: '{{cart.totalFee}}' as any,
      feeDisplay: '{{globals.footer.feeDisplay}}',
      feeColor: 'primary.contrastText',
      discountColor: 'primary.contrastText',
    },
    styles: {
      padding: '0',
      justifyContent: 'end',
      alignItems: 'baseline',
      '.LabelStartContent:nth-child(1)': {
        textTransform: 'none',
        fontSize: '12px',
      },
      '.LabelStartContent:nth-child(2)': {
        textTransform: 'none',
        fontSize: '16px',
      },
    },
  },
  endLabel: {
    label: `{{cart.itemsTotal}} ${TextLibrary.CART_ITEMS_COUNTER}`,
    icon: 'CmArrowCircleRight',
    price: undefined,
    styles: {
      textTransform: 'none ',
    },
  },
}
